/* eslint-disable no-param-reassign */
import axios from 'axios';
import qs from 'qs';
import { userExpired } from 'redux-oidc';
import { RoutesEnum } from 'src/shared/enums/routes.enum';
import ConsoleHelper from '../helpers/consoleHelper';
import userManager from '../identity/userManager';
import { persistor, store } from '../redux/store';

declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface AxiosResponse<T = any> extends Promise<T> {}
}

let unauthorizedLogoutPromise: Promise<void> | null = null;

const redirectToLogin = () => {
  if (window.location.pathname !== RoutesEnum.Login) {
    window.location.assign(RoutesEnum.Login);
  }
};

const handleUnauthorizedResponse = async () => {
  if (!unauthorizedLogoutPromise) {
    unauthorizedLogoutPromise = (async () => {
      try {
        await userManager.removeUser();
        store.dispatch(userExpired());
        await userManager.clearStaleState();
        await persistor.flush();
      } catch (error) {
        ConsoleHelper.warn('Failed to clear invalid session after 401 response', error);
      } finally {
        redirectToLogin();
      }
    })();
  }

  await unauthorizedLogoutPromise;
};

const applyAuthorizationHeader = (config: any) => {
  const { oidc } = store.getState();
  config.headers = config.headers ?? {};

  if (oidc?.user?.access_token) {
    config.headers.Authorization = `Bearer ${oidc.user.access_token}`;
  } else {
    delete config.headers.Authorization;
  }

  return config;
};

// AXIOS SETUP INSTANCE
export const getBaseURL = async () => {
  /*  const parts = window.location.hostname.split('.');
  let lastIndex = -2;
  const last = parts[parts.length - 1];
  const isLocalhost = last === 'localhost';
  if (isLocalhost) {
    lastIndex = -1;
  }
  const subdomain = parts.slice(0, lastIndex).join('.');
 
  if (!subdomain) return `https://apptech.easystaff.se/api/v1`;
 
  return `https://${subdomain}.easystaff.se/api/v1`; */

  return `https://${'apptech'}.easystaff.se/api/v1`;
};

// CREATE AXIOS INSTANCE
export const AxiosInstance = axios.create({
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// AXIOS PARAMS
AxiosInstance.defaults.paramsSerializer = (params: unknown) => {
  const formatted = qs.stringify(params);
  return formatted;
};

// AXIOS REQUEST
AxiosInstance.interceptors.request.use(
  async (config: any) => {
    // ConsoleHelper.log('intercetor request: ', config);
    // config.headers['X-Language'] = getAppLanguage() ?? 'EN';
    // config.headers['X-Version'] = '2.0.0|10146|2022-01-09';
    // config.baseURL = await getBaseURL();
    return applyAuthorizationHeader(config);
  },
  (error: any) => Promise.reject(error),
);

// AXIOS RESPONSE
// // Destruct data from response - returnerar res.data så att vyer kan kolla res.Results direkt
// const handleResponse = ({ data, request }: AxiosResponse) => {
//   return data;
// };
// AxiosInstance.interceptors.response.use(handleResponse);

AxiosInstance.interceptors.response.use(
  (res: any) => {
    // ConsoleHelper.log('axios-interceptors response::', res);
    return res.data;
  },
  async (err: any) => {
    if (err?.response?.status === 401) {
      await handleUnauthorizedResponse();
    }
    return Promise.reject(err.response ?? err);
  },
);

export default {
  get: AxiosInstance.get,
  post: AxiosInstance.post,
  put: AxiosInstance.put,
  delete: AxiosInstance.delete,
  patch: AxiosInstance.patch,
};

// Interceptors form-data
// Axios instance form-data i interceptors.ts
export const AxiosInstanceFormData = axios.create({
  timeout: 20000,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// Request
AxiosInstanceFormData.interceptors.request.use(
  async (config: any) => {
    // ConsoleHelper.log('intercetor request form-data: ', config);
    return applyAuthorizationHeader(config);
  },
  (error) => Promise.reject(error),
);

// response
AxiosInstanceFormData.interceptors.response.use(
  (res: any) => {
    // ConsoleHelper.log('axios-interceptors response form-data::', res);
    return res.data;
  },
  async (err: any) => {
    if (err?.response?.status === 401) {
      await handleUnauthorizedResponse();
    }
    return Promise.reject(err.response ?? err);
  },
);
